import { Component, Input, OnInit, ViewChild, OnDestroy } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { AppGlobals } from '@app/app.global';
import { AuthService, SharedService } from '@app/core';
import { ProfileService } from '@app/modules/profile/services';
import { PmdModal } from '@app/shared/propeller';
import { Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";

@Component({
  selector: 'app-profile-setup-modal',
  templateUrl: './profile-setup-modal.component.html',
  styleUrls: ['./profile-setup-modal.component.scss']
})
export class ProfileSetupModalComponent implements OnInit, OnDestroy {
    private destroy$ = new Subject<void>();
  @ViewChild(PmdModal, { static: true }) profileSetupModal!: PmdModal;
  @Input() currentStep = 1;
  personalInfoFormGroup: FormGroup;
  companyInfoFormGroup: FormGroup;
  isSubmitAttemptedForPersonalInfo = false;
  isSubmitAttemptedForCompanyInfo = false;
  isSubmitting = false;

  constructor(private readonly fb: FormBuilder,
    public appGlobals: AppGlobals,
    public sharedService: SharedService,
    private readonly profileService: ProfileService,
    private readonly authService: AuthService) { }

  ngOnInit(): void {
    this.initializePersonalInfoForm();
    this.initializeCompanyInfoForm();
  }

  ngAfterViewInit(): void {
    this.getCurrentStep();

  }

  initializePersonalInfoForm() {
    this.personalInfoFormGroup = this.fb.group({
      name: ['', [Validators.required, Validators.pattern(this.appGlobals.regex.fullname)]],
      email: ['', [Validators.required, Validators.pattern(this.appGlobals.regex.email)]],
      mobile: ['', [Validators.required, Validators.pattern(this.appGlobals.regex.mobile)]],
    });
  }

  initializeCompanyInfoForm() {
    this.companyInfoFormGroup = this.fb.group({
      companyName: ['', [Validators.required, Validators.maxLength(128)]],
      gst: ['', [Validators.pattern(this.appGlobals.regex.gst)]],
      pan: ['', [Validators.required, Validators.pattern(this.appGlobals.regex.pan)]],
    });
  }

  get fPersonalInfo() {
    return this.personalInfoFormGroup.controls;
  }

  get fCompanyInfo() {
    return this.companyInfoFormGroup.controls;
  }

  getCurrentStep() {
    // get step data from localstorage
    const stepData = this.getStepData();
    const completedStep = localStorage.getItem('profileSetupCurrentPage');

    if (stepData && completedStep != 'completed') {
      // patch step data
      this.currentStep = stepData.currentStep + 1;

      if (stepData.stepOneData) {
        this.patchPersonalInfoForm(stepData.stepOneData);
      }
      if (stepData.stepTwoData) {
        this.patchCompanyInfoForm(stepData.stepTwoData);
      }
      this.profileSetupModal.show();
    } else if (!this.authService.user.isOnboarded) { // if user is not onboarded then show setup profile
      this.currentStep = this.authService.user.profileSetupCurrentPage == 'page1' ? 1 : 2;

      const formData = {
        email: this.authService.user.email ?? '',
        mobile: this.authService.user.mobile ?? '',
      }
      this.patchPersonalInfoForm(formData);
      this.profileSetupModal.show();
    }

  }

  getStepData() {
    return JSON.parse(localStorage.getItem('setupProfileData'));
  }

  setStepData(stepData: any) {
    localStorage.setItem('setupProfileData', JSON.stringify(stepData));
  }

  /**
   * This method will set PAN number from GST Number.
   */
  onGSTAdd() {
    this.fCompanyInfo.gst.setValue(this.fCompanyInfo.gst.value?.toUpperCase());
    if (this.fCompanyInfo.gst.valid) {
      this.fCompanyInfo.pan.setValue(this.fCompanyInfo.gst.value?.substring(2, 12)?.toUpperCase());
    }
  }

  patchPersonalInfoForm(response) {
    this.personalInfoFormGroup.patchValue({
      name: response.name ?? '',
      email: response.email ?? this.authService.user.email ?? '',
      mobile: response.mobile ?? ''
    });

    // If email or mobile is already there then need to disable it.
    if (response.email) {
      this.fPersonalInfo.email.disable();
    }
    if (response.mobile) {
      this.fPersonalInfo.mobile.disable();
    }
  }

  patchCompanyInfoForm(response) {
    this.companyInfoFormGroup.patchValue({
      companyName: response.companyName ?? '',
      gst: response.gst ?? '',
      pan: response.pan ?? ''
    })
  }

  goToNextStep() {
    if (this.currentStep == 1) {
      this.isSubmitAttemptedForPersonalInfo = true;
      if (this.personalInfoFormGroup.valid) {
        const formValue = this.personalInfoFormGroup.getRawValue();
        formValue.currentStep = this.currentStep;

        // set step data in local storage
        const setupProfileData = {
          currentStep: 1,
          stepOneData: formValue
        }
        this.setStepData(setupProfileData);
        this.isSubmitting = false;
        this.currentStep += 1;
      }
    } else if (this.currentStep == 2) {
      this.isSubmitAttemptedForCompanyInfo = true;
      if (this.companyInfoFormGroup.valid) {
        const formValue = this.companyInfoFormGroup.getRawValue();
        formValue.currentStep = this.currentStep;

        // set step data in local storage
        const setupProfileDataFromStorage = this.getStepData();
        if (setupProfileDataFromStorage) {
          setupProfileDataFromStorage.currentStep = 2;
          setupProfileDataFromStorage.stepTwoData = formValue;
          this.setStepData(setupProfileDataFromStorage);
        }

        const stepOneFormValue = this.personalInfoFormGroup.getRawValue();
        stepOneFormValue['step'] = 'page1';
        this.isSubmitting = true;
        this.profileService.profileSetupSteps(stepOneFormValue).pipe(takeUntil(this.destroy$)).subscribe((response) => {
                    if (response) {
                      let stepTwoFormValue: any = this.companyInfoFormGroup.getRawValue();
                      stepTwoFormValue['step'] = 'page2';
                      stepTwoFormValue['skip'] = false;

                      this.profileService.profileSetupSteps(stepTwoFormValue).pipe(takeUntil(this.destroy$)).subscribe((response) => {
                                                if (response) {
                                                  const requestBody = {
                                                    step: "page3",
                                                    startTrial: true
                                                  }
                                                  this.profileService.profileSetupSteps(requestBody).pipe(takeUntil(this.destroy$)).subscribe((response) => {
                                                                                                        if (response) {
                                                                                                          this.isSubmitting = false;
                                                                                                          console.log('response:', response);
                                                                                                          this.sharedService.successAlert("Profile setup completed successfully");
                                                                                                          this.profileSetupModal.hide();
                                                                                                          if (response.user) {
                                                                                                            if (response.user.authorizer) {
                                                                                                              if (response.user.profileSetupCurrentPage == 'completed') {
                                                                                                                localStorage.setItem('profileSetupCurrentPage', 'completed');
                                                                                                              }
                                                                                                              this.authService.authenticate(
                                                                                                                {
                                                                                                                  ...response.user.authorizer,
                                                                                                                  user: this.sharedService.parseJsonValue(response.user.authorizer.user),
                                                                                                                  expiry: new Date(response.user.authorizer.expiry)
                                                                                                                }, true);

                                                                                                              setTimeout(() => {
                                                                                                                window.location.reload();
                                                                                                              }, 200);
                                                                                                            }
                                                                                                          }
                                                                                                        }
                                                                                                      }, (error) => {
                                                                                                        this.currentStep = 2;
                                                                                                        this.sharedService.errorAlert(error);
                                                                                                        this.isSubmitting = false;
                                                                                                      })
                                                }
                                              }, (error) => {
                                                this.currentStep = 2;
                                                this.sharedService.errorAlert(error);
                                                this.isSubmitAttemptedForCompanyInfo = false;
                                                this.isSubmitting = false;
                                              });
                    }
                  }, (error) => {
                    this.currentStep = 1;
                    this.sharedService.errorAlert(error);
                    this.isSubmitAttemptedForPersonalInfo = false;
                    this.isSubmitting = false;
                  });
      }
    }
  }

  /**
   * Navigates to previous step
   */
  goToBackStep() {
    this.currentStep -= 1;
  }

  onLogout(): void {
    this.profileSetupModal?.hide();
    this.authService.logout();
  }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }
}